import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Star, Waves, AlertTriangle, Send, ArrowLeft, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const RATING_LABELS = { 1: "Deludente", 2: "Così così", 3: "Buona", 4: "Molto buona", 5: "Eccellente!" };

export default function Recensione() {
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get("pratica");
  const [state, setState] = useState(leadId ? "loading" : "invalid");
  const [firstName, setFirstName] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Lascia una recensione — ENEA Specialist";
    if (!leadId) return;
    api.get(`/premium-review/context/${leadId}`)
      .then(({ data }) => {
        setFirstName(data.first_name || "");
        if (!data.eligible) setState(data.reason === "not_premium" ? "processing" : "invalid");
        else if (data.already_reviewed) setState("already");
        else setState("form");
      })
      .catch(() => setState("invalid"));
  }, [leadId]);

  const submit = async () => {
    if (submitting) return;
    if (!rating) { toast.error("Seleziona da 1 a 5 stelle."); return; }
    setSubmitting(true);
    try {
      await api.post("/premium-review", { lead_id: leadId, rating, comment: comment.trim() || null });
      setState("done");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invio non riuscito, riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F9FC]" data-testid="review-page">
      <header className="sticky top-0 z-40 bg-[#0b2545] text-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#A9D6E5] to-[#14B8A6] flex items-center justify-center">
              <Waves className="w-4 h-4 text-[#0b2545]" />
            </span>
            <span className="text-sm font-semibold tracking-tight leading-tight">
              Home Exchange Copilot AI
              <span className="block text-[10px] font-normal text-[#A9D6E5]">Gruppo Vela</span>
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[#A9D6E5] hover:text-white transition">
            <ArrowLeft className="w-3.5 h-3.5" /> Torna alla home
          </Link>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl shadow-slate-200/60 p-8">
          {state === "loading" && (
            <div className="text-center" data-testid="review-loading">
              <Loader2 className="w-10 h-10 text-[#14B8A6] animate-spin mx-auto" />
              <p className="mt-3 text-sm text-slate-600">Verifica della pratica in corso…</p>
            </div>
          )}

          {state === "invalid" && (
            <div className="text-center" data-testid="review-invalid">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
              <h1 className="mt-4 text-xl font-display font-semibold text-[#0b2545]">Link recensione non valido</h1>
              <p className="mt-2 text-sm text-slate-600">Usa il link personale che trovi nell&apos;email della tua Valutazione Premium.</p>
            </div>
          )}

          {state === "processing" && (
            <div className="text-center" data-testid="review-processing">
              <Clock className="w-10 h-10 text-[#14B8A6] mx-auto" />
              <h1 className="mt-4 text-xl font-display font-semibold text-[#0b2545]">La tua pratica è in elaborazione</h1>
              <p className="mt-2 text-sm text-slate-600">Potrai lasciare la recensione appena riceverai la brochure Premium via email. Riprova tra qualche minuto.</p>
            </div>
          )}

          {state === "already" && (
            <div className="text-center" data-testid="review-already">
              <CheckCircle2 className="w-10 h-10 text-[#14B8A6] mx-auto" />
              <h1 className="mt-4 text-xl font-display font-semibold text-[#0b2545]">Hai già lasciato la tua recensione</h1>
              <p className="mt-2 text-sm text-slate-600">Grazie{firstName ? ` ${firstName}` : ""}! Il tuo parere è prezioso per noi e per gli altri proprietari. ⛵</p>
            </div>
          )}

          {state === "form" && (
            <div data-testid="review-form">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0d9488]">⭐ Recensione verificata · Cliente Premium</div>
              <h1 className="mt-2 text-2xl font-display font-semibold text-[#0b2545]">
                {firstName ? `${firstName}, com'è andata?` : "Com'è andata?"}
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Hai ricevuto la tua <b>Valutazione Premium ENEA Specialist</b>: raccontaci in 30 secondi cosa ne pensi.
                La tua recensione (con nome e stelle) potrà essere mostrata sul nostro sito.
              </p>

              <div className="mt-6">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">Il tuo voto *</div>
                <div className="flex items-center gap-1.5" data-testid="review-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setRating(n)}
                      onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                      className="p-1 transition-transform hover:scale-110" data-testid={`review-star-${n}`}>
                      <Star className={`w-9 h-9 ${n <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                    </button>
                  ))}
                  {(hover || rating) > 0 && (
                    <span className="ml-2 text-sm font-semibold text-[#0b2545]" data-testid="review-rating-label">{RATING_LABELS[hover || rating]}</span>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">Racconta la tua esperienza <span className="text-slate-400 normal-case">(opzionale)</span></div>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} maxLength={600}
                  placeholder="Es. La brochure spiega ogni euro del valore di casa mia: finalmente un numero che posso difendere…"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50/70 text-sm text-[#0b2545] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/40 resize-none"
                  data-testid="review-comment" />
              </div>

              <button type="button" onClick={submit} disabled={submitting}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c] disabled:opacity-60"
                data-testid="review-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Invia la recensione
              </button>
              <p className="mt-3 text-[10px] text-slate-400 text-center">La recensione viene verificata dal team prima della pubblicazione sul sito.</p>
            </div>
          )}

          {state === "done" && (
            <div className="text-center" data-testid="review-done">
              <span className="w-16 h-16 rounded-full bg-[#14B8A6]/10 border border-[#14B8A6]/30 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-9 h-9 text-[#14B8A6]" />
              </span>
              <h1 className="mt-4 text-2xl font-display font-semibold text-[#0b2545]">Grazie{firstName ? ` ${firstName}` : ""}! 🙏</h1>
              <p className="mt-2 text-sm text-slate-600">La tua recensione è stata ricevuta: dopo una rapida verifica del team sarà pubblicata sul sito.</p>
              <Link to="/" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c]" data-testid="review-done-home">
                Torna alla home
              </Link>
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-[11px] text-slate-500">Gruppo Vela — Vele Cinque S.r.l. · Recensioni verificate dei clienti Premium</p>
      </div>
    </div>
  );
}
